import React, { PureComponent } from 'react';
import { get, isEmpty, merge } from 'lodash';
import { array, arrayOf, bool, object, shape, string } from 'prop-types';
import { Provider } from 'react-redux';
import MediaQuery from 'react-responsive';
import classNames from 'classnames';
import { api as collectApi } from '@fiverr-private/collect';
import { computeHighlights } from '@fiverr-private/experts';
import { GigPageContextProvider } from '@fiverr-private/gig_page_context';
import { Breadcrumbs } from '@fiverr-private/gig_page_nav';
import { PricingProvider } from '@fiverr-private/price_upfront_components';
import { getContext } from '@fiverr-private/fiverr_context';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import { getTranslateOnLoad } from '@fiverr-private/machine_translation';
import { Stack } from '@fiverr-private/layout_components';
import { theme } from '@fiverr-private/theme';
import { validateReviewsSnippet } from '@fiverr-private/gig_reviews';
import { sectionScroller, TOP_PAGE, Waypoint } from '@fiverr-private/gig_page_waypoint_navigation';
import { AISummaryMode } from '@fiverr-private/gig_seller_card';
import createGigStore from '../../store';
import {
    getCurrentPackage,
    getPackageById,
    isRecurringGig,
    isSinglePackage,
    isTriplePackage,
} from '../../reducers/packages';
import { isActiveMilestonePay, workProcessHasMilestones } from '../../reducers/workProcess';
import { updateCollections } from '../../actions/collections';
import { updateOnlineStatus } from '../../actions/isOnline';
import dynamicAllocationService from '../../utils/experiments/dynamicAllocationService';
import { manageRecentlyViewed } from '../../utils/recentlyViewedPages/guestPageViews';
import eventsReporter from '../../utils/reporter';

import { getSellerLanguageParams } from '../../utils/sellerLanguage';
import BrowserDetector from '../../utils/browserDetector';
import fetchIsOnline from '../../utils/fetchIsOnline';

import { validateOrderDetails, validatePackagesTable } from '../../utils/mainComponentsValidator';
import BiEvents from '../../utils/biEvents';
import GigCurrencyConverter from '../../utils/gigCurrencyConverter';
import { MP_COMPARE_PACKAGES_VIEW, MP_SHOW_PAGE_NAME } from '../../utils/mixPanelEvents';
import {
    BQ_LOCAL_CATALOG_GIG_TRANSLATION_LAYER_VIEWED,
    BQ_SELLER_BUYER_SPEAK_SAME_LANGUAGE,
} from '../../utils/bigQueryEvents';
import { isGigApproved, isGigPaused } from '../../utils/gigStatus';
import { isFiverrChoice } from '../../utils/fiverrChoice';
import { addChoiceView, removeChoiceView } from '../../utils/fiverrChoice/choiceViewLocalStorageManager';
import { isLoggedInUser } from '../../utils/isLoggedInUser';
import Logger from '../../utils/logger';
import PathfinderWrapper from '../../utils/pathfinderWrapper';
import { BASIC_PACKAGE_ID } from '../../utils/packagesUtils/constants';
import { GIG_NAV_MENU } from '../../utils/events';
import { MEDIA_GRID_MD } from '../../utils/constants';
import { MIXPANEL_EVENT_TYPES } from '../../types/mixpanelEventTypes';
import { getMilestoneType } from '../../utils/workProcess';
import { metric } from '../../utils/metric';
import OtherGigsBy from '../OtherGigsBy';
import PausedGig from '../PausedGig';
import Seo from '../Seo';
import Sidebar from '../Sidebar';
import PersonalizationWrapper from '../PersonalizationWrapper';
import FloatingContactSeller from '../ContactSeller/FloatingContactSeller';
import SellerBanner from '../SellerBanner';
import CouponBannerContainer from '../CouponBannerContainer';
import PromotionModal from '../PromotionModal';
import MachineTranslationPreviewBanner from '../Overview/MachineTranslationPreviewBanner';
import { PromotedCarousel } from '../PromotedCarousel/PromotedCarousel';
import { toggleUGC } from '../../actions/ugc';
import { buildTranslationLayerBQProps } from '../../utils/ugc/buildTranslationLayerBQProps';
import { buildSourceInfo } from '../../utils/ugc/buildSourceInfo';
import { initClientExperiments } from '../../utils/experiments/clientExperiments';
import { clientExperimentsConfig } from '../../utils/experiments/clientExperimentsConfig';
import { isExperientialGigTheme } from '../../utils/theme/isExperientialGigTheme';
import { NON_EXP_GIGS_SUB_CATEGORIES } from '../../utils/constants/nonExpGigsSubCategories';
import { isNonExperientialGigTheme } from '../../utils/theme/isNonExperientialGigTheme';
import { shouldShowPromotionModal } from '../PromotionModal/utils';
import { ConsultationDataPropType } from '../ContactSeller/constants';
import { calcAgencyData } from '../../utils/calcAgenciesData/calcAgenciesData';
import { trackGigPageLoad, trackWorkflowGigPageRecordingRequested } from '../../utils/gtmTracking/gtmTracking';
import { getPriceDisplayStatus } from '../../utils/priceUpfront';
import { AISummaryEligibility } from '../../utils/constants/AISummaryEligibility';
import { ROLLOUTS } from '../../types/rollouts';
import { isAllocated, inExperiment } from '../../utils/experiments';
import { MEERKATS_AI_SUMMARY } from '../../utils/experiments/experimentsEnum';
import {
    reportAISummaryToggle,
    reportAISummaryImpression,
    reportAISummaryControlImpression,
} from '../../utils/biEvents/bianka';
import {
    BQ_DSA_NONCOMPLIANT_INTERACTION_STOPPED,
    FloatingChatDataPropType,
    LOCAL_CATALOG_PREVIEW_STATES,
    NON_EXP_BQ_PACKAGE_TABLE_SHOW_PARAMS,
    SellerCardPropType,
    SellerHourlyEligibilityPropType,
} from './constants';

import { buildShutterStockExtrasEventData } from './events';

import './style.scss';

class GigPage extends PureComponent {
    constructor(props) {
        super(props);

        const { general, overview } = this.props;

        if (!general) {
            // If general params is missing throw an error and don't render the page
            throw Error('Missing general data');
        }

        this.wrapChildren = this.wrapChildren.bind(this);
        this.shouldRenderNewProBanner = this.shouldRenderNewProBanner.bind(this);
        this.isBusinessLayout = this.isBusinessLayout.bind(this);
        this.getUpmarketBannerVariants = this.getUpmarketBannerVariants.bind(this);

        this.store = createGigStore(this.props);
        this.useStickyLayout = this.shouldUseStickyLayout();
        this.sectionScroller = sectionScroller();

        // used for unwrapping/rewrapping
        this.orphans = [];
        this.displayContents = true;
        this.isMediaMd = true;

        const packagesCount = get(this.store.getState(), 'packages.packageList.length', 3);

        const { locale, browserLanguage, countryCode } = getContext();
        this.sellerLanguageParams = getSellerLanguageParams({
            buyer: { locale, browserLanguage, countryCode },
            seller: overview.seller,
        });

        this.biEvents = new BiEvents({
            general,
            packagesCount,
            paymentType: this.paymentType,
        });
        this.logger = Logger.init();
        this.pathfinderWrapper = new PathfinderWrapper(this.logger);
        dynamicAllocationService.init();
    }

    getChildContext() {
        const {
            general,
            currency,
            choiceEligibilities,
            limitedEdition,
            currentUser,
            tags,
            overview,
            rollouts,
            seller,
            highlightsData,
            instantOrderSettings,
            gigSellerTranslation,
            gigTheme,
            machineTranslationFromServer,
            moreServices,
            topNav,
            coupon,
            studio,
            consultationData,
            sellerCard,
            regionCode,
            sellerHourlyEligibility,
            floatingChatData,
            isPriceUpfrontEnabled,
        } = this.props;

        const { gigId, sellerId } = general;
        const { locale, browserLanguage, countryCode } = getContext();
        const highlights =
            highlightsData &&
            computeHighlights({
                sellerData: seller,
                collaborateData: highlightsData.collaborateData,
                saveData: highlightsData.saveData,
                matchingIndustriesData: highlightsData.matchingIndustriesData,
                locale,
                browserLanguage,
                countryCode,
                hideProVerifiedHighlight: true,
            });

        const { agency, isAgency } = this.buildAgencyData();
        const priceDisplayStatus = getPriceDisplayStatus({regionCode, isPriceUpfrontEnabled});

        return {
            biEvents: this.biEvents,
            biankaEvents: {
                reportAISummaryToggle: (expanded) => reportAISummaryToggle({ expanded, gigId, sellerId }),
                reportAISummaryImpression: () => reportAISummaryImpression({ gigId, sellerId }),
                reportAISummaryControlImpression: () => reportAISummaryControlImpression({ gigId, sellerId }),
            },
            general,
            logger: this.logger,
            currencyConverter: new GigCurrencyConverter(currency),
            pathfinderWrapper: this.pathfinderWrapper,
            choiceEligibilities,
            limitedEdition,
            currentUser,
            seller: {
                achievement: sellerCard.achievement,
                profilePhoto: sellerCard?.profilePhoto,
                proficientLanguages: overview.seller.proficientLanguages,
                isVerified: seller.isVerified,
                photoUrl: seller.photoUrl,
                introVideoUrl: seller.introVideo?.url,
                highlights,
                displayName: seller?.user?.profile?.displayName,
                username: overview?.seller?.username || overview?.seller?.name,
                isPro: seller?.isPro,
                portfolios: seller?.portfolios,
                vacation: seller?.vacation,
                isOnVacation: seller?.isOnVacation,
                agency,
                isAgency,
                responseTime: sellerCard?.responseTime,
                sellerHourlyEligibility: sellerHourlyEligibility?.sellerHourlyEligible,
                hourlyRate: seller?.hourlyRate,
            },
            gig: {
                tags,
                instantOrderSettings,
                gigCollectedCount: topNav?.gigCollectedCount,
                rating: overview?.gig?.rating,
                ratingsCount: overview?.gig?.ratingsCount,
                isRestrictedByRegion: overview?.gig?.isRestrictedByRegion,
            },
            isBusinessLayout: this.isBusinessLayout(),
            isProRebrandingLayout: true,
            sellerLanguageParams: this.sellerLanguageParams,
            rollouts,
            gigSellerTranslation: {
                gigTitle: gigSellerTranslation?.localizedTitle,
                description: gigSellerTranslation?.localizedDescription,
                isEnabled: gigSellerTranslation?.isEnabled,
            },
            gigTheme,
            machineTranslationFromServer,
            moreServices,
            coupon,
            sectionScroller: this.sectionScroller,
            studio,
            consultationData,
            priceDisplayStatus,
            floatingChatData,
            sellerCard,
        };
    }

    componentDidMount() {
        this.setDisplayContentsSupport();
        this.wrapChildren();
        this.reportPageView();
        this.gtmReport();
        this.updateChoiceViewStorage();
        this.initBrowserDetector();
        this.fetchCollections();
        this.reportBuyerSpeakSellerLanguage();
        this.enrichReporter();
        this.initClientExperiments();

        this.manageRecentlyViewedPages();
        this.fetchOnlineStatus();
        this.reportStudioGig();
        this.initStickyTranslation();
        this.logWarningCustomExtra();

        this.reportDsaNonCompliantInteraction();
    }

    buildAgencyData() {
        const { seller } = this.props;
        const { isAgency, agency } = calcAgencyData({ seller });

        return { agency, isAgency };
    }

    initClientExperiments() {
        const { rollouts } = this.props;
        const store = this.store.getState();

        initClientExperiments(rollouts, clientExperimentsConfig, { store });
    }

    manageRecentlyViewedPages() {
        const { general } = this.props;
        const { gigId } = general;

        manageRecentlyViewed(gigId, this.pathfinderWrapper);
    }

    setDisplayContentsSupport() {
        if (window.CSS && window.CSS.supports) {
            this.displayContents = CSS.supports('display: contents');
        } else if (window.supportsCSS) {
            // Opera's old version of css detection
            this.displayContents = window.supportsCSS('display:contents');
        } else {
            this.displayContents = false;
        }
    }

    reportPageView() {
        const { packages } = this.store.getState();
        const { buyerAssumedLanguage, sellerCountryCode, sellerValidatedLanguages } = this.sellerLanguageParams;
        const { portfolio, reviews, notableClients, general } = this.props;
        const {
            seller: { highlights },
        } = this.getChildContext();
        const sellerLanguagesStringArray = sellerValidatedLanguages.map(
            (languageObj) => `${languageObj.name} - ${languageObj.level}`
        );
        const minPackagePrice = getPackageById(packages, BASIC_PACKAGE_ID)?.price;
        const shutterStockEventData = buildShutterStockExtrasEventData(packages);
        const withReviewsSnippet = this.shouldRenderReviews() && validateReviewsSnippet(reviews);

        this.biEvents.sendMixPanelEvent({
            mpEventType: MIXPANEL_EVENT_TYPES.PAGE_VIEW,
            eventName: MP_SHOW_PAGE_NAME,
            params: {
                buyerAssumedLanguage,
                sellerCountryCode,
                sellerValidatedLanguages: sellerLanguagesStringArray,
                minPackagePrice: minPackagePrice / 100,
                shutterStockEventData,
                isNex: NON_EXP_GIGS_SUB_CATEGORIES.has(general.subCategoryId),
                highlightsCount: highlights?.length ?? 0,
                withReviewsSnippet,
                withPortfolio: !!portfolio?.projectsCount,
                notableClientsCount: notableClients?.length ?? 0,
                reviewsCount: reviews?.reviewsCount ?? 0,
            },
        });
    }

    reportBuyerSpeakSellerLanguage() {
        const { shouldShowSellerLanguage, commonLanguage } = this.sellerLanguageParams;

        if (shouldShowSellerLanguage) {
            this.biEvents.sendBigQueryEvent({
                eventName: BQ_SELLER_BUYER_SPEAK_SAME_LANGUAGE,
                params: {
                    commonLanguage,
                },
            });
        }
    }

    reportStudioGig() {
        const { isStudio } = this.props.general;

        isStudio && metric.count('studio_gig.impression');
    }

    enrichReporter() {
        const { general } = this.props;
        const { gigId } = general;
        const enrichment = { gig: { id: gigId } };

        eventsReporter.enrich(enrichment);
    }

    gtmReport() {
        const { general, gigSellerTranslation } = this.props;
        const {
            gigId,
            gigTitle: originalGigTitle,
            isPro,
            categorySlug,
            subCategorySlug,
            nestedSubCategorySlug,
        } = general;
        const { packages } = this.store.getState();
        const basicPackage = getPackageById(packages, BASIC_PACKAGE_ID);
        const gigTitleParam = gigSellerTranslation?.gigTitle || originalGigTitle;

        trackGigPageLoad({
            gigId,
            gigTitle: gigTitleParam,
            isPro,
            categorySlug,
            subCategorySlug,
            nestedSubCategorySlug,
            basePrice: basicPackage ? basicPackage.price : 0,
        });

        if (this.hasWorkflow) {
            trackWorkflowGigPageRecordingRequested();
        }
    }

    updateChoiceViewStorage() {
        const { general, choiceEligibilities } = this.props;
        const { gigId } = general;
        const { queryParameters, abTests } = getContext();

        if (!isLoggedInUser()) {
            return;
        }

        isFiverrChoice({ queryParameters, abTests, choiceEligibilities })
            ? addChoiceView(gigId)
            : removeChoiceView(gigId);
    }

    initBrowserDetector() {
        BrowserDetector.init();
    }

    reportPackagesTableVisible() {
        const baseBqData = this.biEvents.getBigQueryEnrichmentData();

        this.biEvents.sendMixPanelEvent({ eventName: MP_COMPARE_PACKAGES_VIEW });
        this.biEvents.sendRawBigQueryEvent(
            merge({}, NON_EXP_BQ_PACKAGE_TABLE_SHOW_PARAMS, {
                user: { id: baseBqData.userId },
                seller: { id: baseBqData.sellerId },
                gig: { id: baseBqData.gigId },
                page: { ctx_id: baseBqData.pageCtxId },
            })
        );
    }

    isPro() {
        const { general } = this.props;

        return general.isPro;
    }

    getUpmarketBannerVariants() {
        const { userId } = getContext();
        const { upmarketSegment } = this.props;

        if (userId) {
            return {
                withDesBanner: upmarketSegment?.proBuType === 'MPFit',
                withMultiVariantBanner: upmarketSegment?.proBuType === 'ProFit',
            };
        }

        return { withDesBanner: false, withMultiVariantBanner: false };
    }

    getAISummaryMode() {
        const { abTests } = getContext();
        const {
            summary,
            visible,
            eligibility_status,
            csm_seller_gig,
        } = this.props.aiSummary;
        const {
            [ROLLOUTS.MEERKATS_AI_SUMMARY_ALL_GIGS]: inAISummaryAllGigsRollout
        } = this.props.rollouts;
        const isGigEligible = inAISummaryAllGigsRollout || !!csm_seller_gig;
        const show = summary.length > 0
            && eligibility_status === AISummaryEligibility.VISIBILITY
            && !!visible
            && isGigEligible;

        if (!show) {
            return AISummaryMode.NONE;
        }

        if (this.isPageOwner()) {
            return AISummaryMode.DISPLAY;
        }

        if (!isAllocated(MEERKATS_AI_SUMMARY, abTests)) {
            return AISummaryMode.NONE;
        }

        return inExperiment(MEERKATS_AI_SUMMARY, abTests)
            ? AISummaryMode.DISPLAY
            : AISummaryMode.IMPRESSION;
    }

    isPageOwner() {
        const { sellerId } = this.props.general;
        const { userId } = getContext();

        return sellerId === userId;
    }

    canWrapUnwrapChildren() {
        return this.sidebar && this.main && !this.displayContents;
    }

    wrapChildren() {
        if (this.canWrapUnwrapChildren()) {
            this.isMediaMd ? this.rewrapChildren() : this.unwrapChildren();
        }
    }

    unwrapChildren() {
        if (!this.sidebar.firstChild) {
            return;
        }

        this.orphans = [];
        while (this.sidebar.firstChild) {
            this.orphans.push(this.sidebar.firstChild);
            this.main.insertBefore(this.sidebar.firstChild, this.main.firstChild);
        }
    }

    rewrapChildren() {
        for (let i = 0; i < this.orphans.length; i++) {
            this.sidebar.appendChild(this.orphans[i]);
        }
    }

    reportDsaNonCompliantInteraction() {
        if (this.props.isRestrictedByRegion) {
            this.biEvents.sendRawBigQueryEvent(
                merge({}, BQ_DSA_NONCOMPLIANT_INTERACTION_STOPPED, {
                    buyer: { id: this.props.buyer.id },
                    seller: { id: this.props.seller.user.id },
                    reason: 'gig_page',
                })
            );
        }
    }

    shouldRenderPackagesTable() {
        const { packages } = this.store.getState();

        return validatePackagesTable({ isTriplePackage: isTriplePackage(packages) });
    }

    isBusinessLayout() {
        const { experience = {} } = getContext();

        return experience.isBusiness;
    }

    shouldRenderOrderDetails() {
        const { packages } = this.store.getState();
        const { general } = this.props;
        const { isLimitedEdition } = general;

        return !isLimitedEdition && validateOrderDetails({ isSinglePackage: isSinglePackage(packages) });
    }

    shouldRenderComparePersonalizedSection() {
        const { general } = this.props;
        const { gigStatus, isLimitedEdition } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);
        return !isPageOwner && isApproved && !isLimitedEdition;
    }

    shouldRenderExplorePersonalizedSection() {
        return !this.isPageOwner();
    }

    shouldRenderNewProBanner() {
        const { seller } = this.props;
        return seller?.isPro;
    }

    shouldRenderLimitedEditionBanner() {
        const isPro = this.isPro();
        const { isLimitedEdition } = this.props.general;

        return isLimitedEdition && !isPro;
    }

    shouldRenderBottomCarousels() {
        const { isLimitedEdition } = this.props.general;

        return !isLimitedEdition;
    }

    shouldUseStickyLayout() {
        const { general } = this.props;

        return !general.isStudio;
    }

    shouldRenderReviews() {
        const { isLimitedEdition } = this.props.general;

        return !isLimitedEdition;
    }

    shouldRenderTranslationLayerPreviewBanner() {
        const { queryParameters } = getContext();
        const { forced_locale, preview_state } = queryParameters;

        return forced_locale && preview_state;
    }

    shouldRenderSellerBanner() {
        const shouldRenderTranslationLayerPreviewBanner = this.shouldRenderTranslationLayerPreviewBanner();
        const isPageOwner = this.isPageOwner();

        return isPageOwner && !shouldRenderTranslationLayerPreviewBanner;
    }

    shouldRenderMachineTranslationPreviewBanner() {
        const { queryParameters } = getContext();
        const { preview_state } = queryParameters;

        return preview_state === LOCAL_CATALOG_PREVIEW_STATES.MACHINE_TRANSLATION_PREVIEW;
    }

    get hasWorkflow() {
        const { workflow } = this.props;

        return !isEmpty(workflow);
    }

    get paymentType() {
        const { workflow } = this.props;
        const state = this.store.getState();
        const isRecurring = isRecurringGig(state.packages);
        const hasWorkflow = !isEmpty(workflow);
        const hasWorkProcess = workProcessHasMilestones(state);
        const activeMilestonePay = isActiveMilestonePay(state);

        return getMilestoneType({
            isRecurring,
            hasWorkflow,
            hasWorkProcess,
            activeMilestonePay,
        });
    }

    async fetchCollections() {
        if (isLoggedInUser()) {
            const { collections } = await collectApi.getCollections();
            this.store.dispatch(updateCollections(collections));
        }
    }

    async fetchOnlineStatus() {
        const isOnline = await fetchIsOnline({
            pathfinderWrapper: this.pathfinderWrapper,
            logger: this.logger,
        });

        this.store.dispatch(updateOnlineStatus(isOnline));
    }

    /**
     * initStickyTranslation activates MT-translation on load if conditions passed
     *  sticky translation is default naming across Fiverr and stands for using MT-translation on load
     *  sticky translation is available only for non en-US locale
     *  */
    async initStickyTranslation() {
        const {
            machineTranslationFromServer,
            general: { gigId, gigStatus },
        } = this.props;
        const isPageOwner = this.isPageOwner();

        const { locale, queryParameters } = getContext();
        const { forced_locale } = queryParameters;
        const isInSellerPreviewMode = isPageOwner && forced_locale;
        const isDefaultLocale = locale === DEFAULT_FIVERR_LOCALE;
        const isMtOnServerEnabled = machineTranslationFromServer?.isEnabled;
        const isGigStatusApproved = isGigApproved(gigStatus);

        if (isDefaultLocale || isMtOnServerEnabled) {
            return;
        }

        /**
         * sticky translation is disabled in own gig preview
         * except of gig-translation preview mode
         * */
        if (isPageOwner && !isInSellerPreviewMode) {
            return;
        }

        const sourceInfo = buildSourceInfo({ componentName: GIG_NAV_MENU, gigId });
        // we enable sticky translation only for approved gigs
        const isTranslationOnLoadEnabled = isGigStatusApproved ? getTranslateOnLoad() : isInSellerPreviewMode;

        if (isTranslationOnLoadEnabled) {
            try {
                this.store.dispatch(await toggleUGC(sourceInfo));
                if (this.isGigSellerTranslationLoaded()) {
                    this.reportGigSellerTranslationFeatureEnabledBIEvent();
                }
            } catch (ignore) {
                /* empty */
            }
        }
    }

    isGigSellerTranslationLoaded() {
        const { ugc = {} } = this.store.getState();
        const { ugcMap } = ugc;

        return ugcMap.translatedBySeller?.enabled;
    }

    reportGigSellerTranslationFeatureEnabledBIEvent() {
        const { ugc = {} } = this.store.getState();
        const { ugcMap } = ugc;
        this.biEvents.sendBigQueryEvent({
            eventName: BQ_LOCAL_CATALOG_GIG_TRANSLATION_LAYER_VIEWED,
            params: buildTranslationLayerBQProps(ugcMap.translatedBySeller),
        });
    }

    logWarningCustomExtra() {
        const { general } = this.props;
        const { gigId } = general;
        const state = this.store.getState();
        const { customExtras, features } = getCurrentPackage(state) || {};
        const featureIds = features?.map((feature) => feature.id);
        customExtras?.forEach(({ id, label, price, buyerTooltip }) => {
            const filterCondition = !!label && !!price && !!buyerTooltip && !featureIds?.includes(id);
            if (!filterCondition) {
                this.logger.warn('Get custom extras method filtered this extra', {
                    gigId,
                    customExtraId: id || 'Custom offer id does not exists',
                    label: label || 'Label does not exists',
                    price: price || 'Price does not exists',
                    buyerTooltip: buyerTooltip || 'BuyerTooltip does not exists',
                    distinctId: !featureIds?.includes(id),
                });
            }
        });
    }

    render() {
        const {
            children,
            description,
            sellerCard,
            portfolio,
            customOrder,
            otherGigs,
            general,
            studio,
            seo,
            faq,
            coupon,
            packages,
            seller,
            promotedGigs = [],
            gigTheme = 'experiential',
            priceUpfrontConfiguration,
            priceUpfrontContext,
            isPriceUpfrontEnabled,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ..._props // in order to silence propTypes errors, this is temp
        } = this.props;

        const { gigStatus, traffiqed, isStudio, gigId } = general;
        const { pathParameters, isTouch } = getContext();
        const { username } = pathParameters;
        const { info, services } = studio;
        const { packageList = [] } = packages;

        const isLoggedIn = isLoggedInUser();
        const isPageOwner = this.isPageOwner();
        const { promotionStatus } = this.props;
        const isPaused = isGigPaused(gigStatus, traffiqed);
        const isPro = this.isPro();
        const { achievement, profilePhoto, hasProfilePhoto, responseTime } = sellerCard;
        const portfolioProps = { portfolio, achievement, isPro, profilePhoto, hasProfilePhoto };

        const shouldRenderSellerBanner = this.shouldRenderSellerBanner();
        const shouldRenderMachineTranslationPreviewBanner = this.shouldRenderMachineTranslationPreviewBanner();
        const showPromotionModal = shouldShowPromotionModal(coupon, gigId);

        const mainContainerClassName = 'gig-page';
        const outerClasses = classNames(
            'gig-page-wrapper',
            'responsive',
            {
                'pro-gig': isPro,
                touch: isTouch,
            },
            {
                experiential: isExperientialGigTheme(gigTheme),
                'non-experiential': isNonExperientialGigTheme(gigTheme),
            }
        );

        const isDefaultLocale = getContext().locale === DEFAULT_FIVERR_LOCALE;

        const shouldRenderUGCTranslateButton = !isDefaultLocale && !isPageOwner;

        const layoutProps = {
            ...this.props,
            shouldRenderUGCTranslateButton,
            username,
            portfolioProps,
            info,
            services,
            isStudio,
            description,
            summary: this.props.aiSummary.summary,
            shouldRenderOrderDetails: this.shouldRenderOrderDetails.bind(this),
            shouldRenderPackagesTable: this.shouldRenderPackagesTable.bind(this),
            shouldRenderReviews: this.shouldRenderReviews.bind(this),
            shouldRenderLimitedEditionBanner: this.shouldRenderLimitedEditionBanner.bind(this),
            shouldRenderNewProBanner: this.shouldRenderNewProBanner.bind(this),
            setState: this.setState.bind(this),
            shouldRenderComparePersonalizedSection: this.shouldRenderComparePersonalizedSection.bind(this),
            useStickyLayout: this.useStickyLayout,
            reportPackagesTableVisible: this.reportPackagesTableVisible.bind(this),
            getUpmarketBannerVariants: this.getUpmarketBannerVariants,
            getAISummaryMode: this.getAISummaryMode.bind(this),
        };

        return (
            <PricingProvider
                priceUpfrontConfiguration={priceUpfrontConfiguration}
                priceUpfrontContext={priceUpfrontContext}
                isPriceUpfrontEnabled={isPriceUpfrontEnabled}
            >
                <GigPageContextProvider {...this.getChildContext()}>
                    <Provider store={this.store}>
                        <div className={outerClasses}>
                        <Seo seo={seo} faq={faq} packagesCount={packageList.length} />
                        <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                            {(matches) => {
                                this.isMediaMd = matches;
                                this.wrapChildren();

                                return false;
                            }}
                        </MediaQuery>

                        <Waypoint source={TOP_PAGE} />

                        <div className="gig-page-top">
                            <PromotedCarousel isLoggedIn={isLoggedIn} isTouch={isTouch} promotedGigs={promotedGigs} />
                            {shouldRenderSellerBanner && <SellerBanner promotionStatus={promotionStatus} />}
                            {shouldRenderMachineTranslationPreviewBanner && <MachineTranslationPreviewBanner />}
                            {!isPageOwner && isPaused && <PausedGig />}
                            {coupon && <CouponBannerContainer coupon={coupon} />}
                        </div>

                        <div className={classNames(mainContainerClassName, { 'coupon-banner': !!coupon })}>
                            <Sidebar
                                useStickyLayout={this.useStickyLayout}
                                customOrder={customOrder}
                                containerClassName={mainContainerClassName}
                                setRef={(ele) => {
                                    this.sidebar = ele;
                                }}
                                studio={studio}
                                seller={seller}
                            />

                            <div
                                className="main"
                                ref={(ele) => {
                                    this.main = ele;
                                }}
                            >
                                {/* need the inline style to override the div margin-bottom forced in gig page .main class, can be removed after the layout will be fixed */}
                                {!this.isBusinessLayout() && (
                                    <Stack style={{ marginBottom: theme.spacing['7'] }} paddingTop="1.5">
                                        <Breadcrumbs />
                                    </Stack>
                                )}
                                {React.cloneElement(children, layoutProps)}
                            </div>
                        </div>

                        <MediaQuery query={`(max-width: ${MEDIA_GRID_MD})`}>
                            {(matches) => (
                                <FloatingContactSeller
                                    responseTime={responseTime}
                                    profilePhoto={profilePhoto}
                                    isMobileView={matches}
                                />
                            )}
                        </MediaQuery>

                        {this.shouldRenderBottomCarousels() && (
                            <aside className="gig-page-bottom">
                                <div className="max-width-container">
                                    <OtherGigsBy
                                        otherGigs={otherGigs}
                                        isLoggedIn={isLoggedIn}
                                        isTouch={isTouch}
                                        studioInfo={info}
                                    />
                                    {this.shouldRenderExplorePersonalizedSection() && (
                                        <PersonalizationWrapper
                                            sectionName={PersonalizationWrapper.SECTION_NAMES.EXPLORE}
                                            className="personalized-content-explore"
                                        />
                                    )}
                                </div>
                            </aside>
                        )}
                        {showPromotionModal && (
                            <PromotionModal coupon={coupon} profilePhoto={profilePhoto} gigId={gigId} />
                        )}
                    </div>
                    </Provider>
                </GigPageContextProvider>
            </PricingProvider>
        );
    }
}

GigPage.propTypes = {
    general: object,
    overview: object,
    currency: object,
    choiceEligibilities: array,
    limitedEdition: object,
    currentUser: object,
    tags: object,
    description: object,
    faq: object,
    reviews: object,
    sellerCard: object,
    rollouts: object,
    topNav: object,
    children: object,
    gallery: object,
    portfolio: object,
    customOrder: object,
    moreServices: object,
    otherGigs: object,
    coupon: object,
    seo: object,
    studio: object,
    repeatScore: object,
    workflow: object,
    notableClients: array,
    voiceOverAiAudition: object,
    packages: object,
    floatingChatData: FloatingChatDataPropType,
    promotionStatus: string,
    seller: object,
    highlightsData: object,
    instantOrderSettings: array,
    gigSellerTranslation: object,
    gigTheme: string,
    machineTranslationFromServer: object,
    newProBanner: object,
    promotedGigs: array,
    consultationData: ConsultationDataPropType,
    regionCode: string,
    sellerHourlyEligibility: SellerHourlyEligibilityPropType,
    isRestrictedByRegion: bool,
    buyer: object,
    upmarketSegment: object,
    priceUpfrontConfiguration: object,
    priceUpfrontContext: object,
    isPriceUpfrontEnabled: bool,
    aiSummary: shape({
        summary: arrayOf(string).isRequired,
        visible: bool,
        eligibility_status: string,
        csm_seller_gig: bool,
    }),
};

GigPage.defaultProps = {
    sellerCard: {},
    studio: {},
    description: {},
    limitedEdition: {},
    packages: {},
    rollouts: {},
    seller: {
        isVerified: false,
    },
    gigSellerTranslation: {},
    gigTheme: 'experiential',
    machineTranslationFromServer: {},
    isRestrictedByRegion: false,
    buyer: {
        buyerId: '',
    },
    upmarketSegment: {},
    aiSummary: {
        summary: [],
    },
};

GigPage.childContextTypes = {
    biEvents: object,
    biankaEvents: object,
    logger: object,
    general: object,
    currencyConverter: object,
    pathfinderWrapper: object,
    choiceEligibilities: array,
    limitedEdition: object,
    currentUser: object,
    gig: object,
    seller: object,
    isBusinessLayout: bool,
    isProRebrandingLayout: bool,
    sellerLanguageParams: object,
    rollouts: object,
    clientExperimentsAllocation: object,
    gigSellerTranslation: object,
    gigTheme: string,
    machineTranslationFromServer: object,
    moreServices: object,
    coupon: object,
    sectionScroller: object,
    studio: object,
    consultationData: ConsultationDataPropType,
    priceDisplayStatus: object,
    floatingChatData: FloatingChatDataPropType,
    isRestrictedByRegion: bool,
    sellerCard: SellerCardPropType,
};

export default GigPage;
